import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { concatMap, map, takeUntil, tap } from 'rxjs/operators';
import { AppService } from '../app.service';
import { NetworkService } from 'src/app/service/network.service';
import { Department, DeptBookService } from 'src/app/service/deptbook.service';

@Injectable()
export class DepartmentSelector {
    public selectedDepartment: Department | null = null;
    public selectedCountry: string | null = null;
    public selectedCity: string | null = null;
    public departments: Department[] = [];
    public countries: string[] = [];
    public cities: string[] = [];

    constructor(
        private deptBook: DeptBookService,
        private networkService: NetworkService,
        private appService: AppService
    ) {}

    initialize(): Observable<void> {
        return of(this).pipe(
            concatMap((selector) =>
                selector.departments.length === 0
                    ? this.reloadDepartmentList()
                    : of(void 0)
            )
        );
    }

    setDepartmentByCode(
        code: string | null,
        until$: Observable<void>
    ): Observable<void> {
        return this.initialize()
            .pipe(takeUntil(until$))
            .pipe(tap(() => this.onDepartmentCode(code)));
    }

    onSelectedCountryChanged(): void {
        this.selectedCity = null;
        this.selectedDepartment = null;

        if (this.selectedCountry !== null) {
            this.selectedCountry = this.selectedCountry.trim().toUpperCase();
            
            this.countries = this.departments.map(d => d.country)
                .filter((c) => c.toUpperCase().startsWith(this.selectedCountry!.toUpperCase()))
                .filter((c, i, self) => self.indexOf(c) === i)
                .sort();
            this.cities = this.departments
                .filter(d => d.country === this.selectedCountry)
                .map(d => d.city)
                .sort();
        }
    }

    onSelectedCityChanged(): void {
        if (this.selectedCity !== null) {
            this.selectedCity = this.selectedCity.trim();
            this.cities = this.departments
                .filter(d => this.selectedCountry && d.country.toUpperCase().startsWith(this.selectedCountry.toUpperCase()))
                .filter(d => d.city.toUpperCase().startsWith(this.selectedCity!.toUpperCase()))
                .map(d => d.city)
                .sort();
            this.selectedCity = this.selectedCity.toUpperCase();
            this.selectedDepartment = this.departments.filter(d => d.city === this.selectedCity)[0] ?? null;
        }
    }

    private getDepartmentList(): Observable<Department[]> {
        return this.appService.departments === null
            ? this.loadDepartmentListFromServer()
            : of(this.appService.departments);
    }

    private loadDepartmentListFromServer(): Observable<Department[]> {
        return this.networkService.waitForInternetConnection().pipe(
            concatMap(() => this.deptBook.list()),
            tap((list) => (this.appService.departments = list))
        );
    }

    private reloadDepartmentList(): Observable<void> {
        return this.getDepartmentList().pipe(
            tap((list) => this.onDepartmentListLoaded(list)),
            map(() => void 0)
        );
    }

    private onDepartmentListLoaded(list: Department[]): void {
        this.departments = list;
        this.countries = this.departments
            .map((d) => d.country)
            .filter((v, i, a) => a.indexOf(v) === i)
            .sort();
    }

    private onDepartmentCode(code: string | null): void {
        let department = this.departments.find((d) => d.code === code);
        if (department) {
            this.selectedCountry = department.country;
            this.onSelectedCountryChanged();
            this.selectedCity = department.city;
            this.onSelectedCityChanged();
        } else {
            this.selectedCountry = null;
            this.onSelectedCountryChanged();
        }
    }
}
